import React from "react";
import {
  Button,
  Pressable,
  Popover,
  AgeGatedPressable,
  Image,
  NavigationMenu,
} from "@gonoodle/gn-universe-ui";
import {
  HeartIcon,
  SearchIcon,
  MenuIcon,
  XIcon,
  SwitchHorizontalIcon,
  UserIcon,
  QuestionMarkCircleIcon,
  LogoutIcon,
} from "@heroicons/react/outline";
import { useRouter } from "next/router";
import { concatenateQueryParams } from "@gonoodle/gn-universe-utils";
import {
  SECTIONS,
  SECTIONS_TYPES,
} from "@gonoodle/gn-universe-analytics-schema/src/constants";
import useMeasure from "react-use-measure";
import { twMerge } from "tailwind-merge";
import NextImage from "next/image";
import { camelize } from "humps";

import { useUser } from "../../contexts/user";
import { useProfile, useCurriculum } from "../../hooks";
import { ROUTE_PREFIX, PROFILE_TYPES, ROUTE_PATHS } from "../../constants";
import { navigation } from "../../fixtures";
import { useLogEvent } from "../../contexts/Analytics";
import {
  getSourcePageAndSourcePageTypeFromReferrer,
  getUtmParamsFromQueryParams,
} from "../../utils/analytics";
import OnSiteMessages from "../OnSiteMessages";
import OnMasqueradingMessage from "../OnMasqueradingMessage";
import Link from "../Link";
import GoNoodleLogo from "../../public/GoNoodleLogo_Registered_Reverse.svg";
import GoNoodleIcon from "../../public/GoNoodleLogo_Icon_Reverse.svg";
import GoNoodleGamesIcon from "../../public/images/gn-games-icon.svg";

const JSURL = require("jsurl");

function NavigationMenuLink({ className, children, ...props }) {
  return (
    <Link legacyBehavior passHref {...props}>
      <NavigationMenu.Link className={className}>
        {children}
      </NavigationMenu.Link>
    </Link>
  );
}

export default function AppBar() {
  const router = useRouter();
  const [isUserMenuOpen, setIsUserMenuOpen] = React.useState(false);
  const [activeMenuItem, setActiveMenuItem] = React.useState(undefined);
  const [navigationMenuListRef, setNavigationMenuListRef] = React.useState(
    null,
  );
  const [activeTriggerOffset, setActiveTriggerOffset] = React.useState();
  const [activeCompactMenuItem, setActiveCompactMenuItem] = React.useState(
    undefined,
  );
  const { user = {}, logout } = useUser();
  const { profile } = useProfile();
  const curriculum = useCurriculum();
  const [bannerRef, bannerBounds] = useMeasure();

  const champ = profile?.currentClassroomChamp;
  const compactMenuKey = "compactMenu";

  const { logEvent: logLogoutEvent } = useLogEvent({
    event: "Logout",
    properties: {},
    options: {
      referrer: () => ({
        sourceElement: SECTIONS_TYPES.NAV,
        sourceName: SECTIONS.LOGOUT_BUTTON,
        ...getSourcePageAndSourcePageTypeFromReferrer(
          router.pathname,
          router.query,
        ),
      }),
    },
  });

  const onTriggerNodeUpdate = (trigger, activeTrigger) => {
    if (trigger && navigationMenuListRef && activeMenuItem === activeTrigger) {
      const navigationMenuListWidth = navigationMenuListRef.offsetWidth;
      const navigationMenuListCenter = navigationMenuListWidth / 2;

      const triggerOffsetRight =
        navigationMenuListWidth -
        trigger.offsetLeft -
        trigger.offsetWidth +
        trigger.offsetWidth / 2;

      setActiveTriggerOffset(
        Math.round(navigationMenuListCenter - triggerOffsetRight),
      );
    } else if (activeMenuItem === "") {
      setActiveTriggerOffset(null);
    }
    return trigger;
  };

  return (
    <div className="bg-black gn-nav z-50 sticky top-0">
      <OnMasqueradingMessage />
      {router.pathname.startsWith(`/${ROUTE_PREFIX.CURRICULUM}/`) ? null : (
        <OnSiteMessages ref={bannerRef} />
      )}

      <div className="flex items-center justify-between h-navbar p-sm container">
        {/* Mobile Nav Start */}
        <NavigationMenu.Root
          value={activeCompactMenuItem}
          onValueChange={(menuItem) => setActiveCompactMenuItem(menuItem)}
          className="block lg:hidden"
        >
          <NavigationMenu.List className="flex items-center list-none">
            <NavigationMenu.Item value={compactMenuKey}>
              <NavigationMenu.Trigger className="mr-sm p-xs text-white hover:text-mint group">
                <div className="group-data-[state=closed]:hidden">
                  <span className="sr-only">Close main menu</span>
                  <XIcon className="block h-7 w-7" aria-hidden="true" />
                </div>

                <div className="group-data-[state=open]:hidden">
                  <span className="sr-only">Open main menu</span>
                  <MenuIcon className="block h-7 w-7 " aria-hidden="true" />
                </div>
              </NavigationMenu.Trigger>

              <NavigationMenu.Content
                forceMount
                className={twMerge(
                  "px-lg pt-md pb-lg overflow-y-auto bg-gray-800 hidden",
                  activeCompactMenuItem === compactMenuKey && "block",
                )}
                style={{
                  maxHeight:
                    bannerBounds?.height > 0
                      ? `calc(100vh - var(--navbar-height) - ${bannerBounds.height}px)`
                      : `calc(100vh - var(--navbar-height))`,
                }}
              >
                <ul className="space-y-md list-none">
                  <li>
                    <NavigationMenuLink
                      to={
                        curriculum
                          ? `/${ROUTE_PREFIX.CURRICULUM}/${curriculum.id}`
                          : "https://supernoodle.gonoodle.com"
                      }
                      events={[
                        {
                          event: "Link Clicked",
                          properties: {
                            title: "SuperNoodle",
                            sourceElement: SECTIONS_TYPES.NAV,
                            sourceName: SECTIONS_TYPES.NAV,
                            ...getUtmParamsFromQueryParams(router.query),
                          },
                        },
                      ]}
                      className="text-transparent whitespace-nowrap bg-clip-text bg-gradient-to-r from-yellow to-pink gn-text-lg font-bold"
                    >
                      SuperNoodle
                    </NavigationMenuLink>
                  </li>

                  {navigation.compactMenu.map((item) => (
                    <li
                      key={item.slug}
                      className="flex items-center gn-text-lg font-bold text-white"
                    >
                      <NavigationMenuLink
                        to={item.slug}
                        events={[
                          {
                            event: "Link Clicked",
                            properties: {
                              title: item.name,
                              sourceElement: SECTIONS_TYPES.NAV,
                              sourceName: SECTIONS_TYPES.NAV,
                              ...getUtmParamsFromQueryParams(router.query),
                            },
                          },
                        ]}
                      >
                        {item.name}
                      </NavigationMenuLink>
                    </li>
                  ))}
                </ul>
              </NavigationMenu.Content>
            </NavigationMenu.Item>

            <NavigationMenu.Item>
              <NavigationMenuLink
                to={ROUTE_PREFIX.HOME}
                events={[
                  {
                    event: "Link Clicked",
                    properties: {
                      title: "GoNoodle Logo",
                      sourceElement: SECTIONS_TYPES.NAV,
                      sourceName: SECTIONS_TYPES.NAV,
                      ...getUtmParamsFromQueryParams(router.query),
                    },
                  },
                ]}
              >
                <NextImage
                  src={GoNoodleIcon}
                  width={51}
                  height={38}
                  className="transform transition hover:scale-110"
                  alt="GoNoodle logo"
                  priority={true}
                />
              </NavigationMenuLink>
            </NavigationMenu.Item>
          </NavigationMenu.List>

          {/* Translate y is used to account for padding around navigation menu trigger so that viewport moves under navbar */}
          <NavigationMenu.Viewport className="absolute w-screen min-h-screen -translate-x-3 translate-y-3 bg-gray-800" />
        </NavigationMenu.Root>
        {/* Mobile Nav End */}

        {/* Desktop Nav Start */}

        <Link
          to={ROUTE_PREFIX.HOME}
          className="hidden lg:block shrink-0 focus:outline-mint"
          events={[
            {
              event: "Link Clicked",
              properties: {
                title: "GoNoodle Logo",
                sourceElement: SECTIONS_TYPES.NAV,
                sourceName: SECTIONS_TYPES.NAV,
                ...getUtmParamsFromQueryParams(router.query),
              },
            },
          ]}
          aria-label="GoNoodle Home"
        >
          <NextImage
            width={150}
            className="transform transition hover:scale-110 "
            src={GoNoodleLogo}
            priority={true}
            alt="GoNoodle logo"
          />
        </Link>

        <NavigationMenu.Root
          value={activeMenuItem}
          onValueChange={(menuItem) => setActiveMenuItem(menuItem)}
          className="hidden lg:block relative mx-auto"
        >
          <NavigationMenu.List
            ref={setNavigationMenuListRef}
            className="flex items-center list-none"
          >
            {navigation.fullMenu.map((menuItem) => {
              const camelizedTitle = camelize(menuItem.title);
              const hasSubMenus = menuItem.sections.length > 1;

              if (hasSubMenus) {
                return (
                  <NavigationMenu.Item
                    value={menuItem.title}
                    key={menuItem.title}
                  >
                    <NavigationMenu.Trigger
                      id={camelizedTitle}
                      aria-controls={camelizedTitle}
                      ref={(node) => onTriggerNodeUpdate(node, menuItem.title)}
                      className="text-white rounded px-sm py-xs gn-text-sm font-bold mr-0.5 transition-colors focus:outline-none focus:ring-2 hover:ring-2 ring-mint"
                    >
                      {menuItem.title}
                    </NavigationMenu.Trigger>

                    <NavigationMenu.Content
                      forceMount
                      asChild
                      className={twMerge(
                        "w-max hidden",
                        activeMenuItem === menuItem.title && "block",
                      )}
                    >
                      <ul
                        id={`${camelizedTitle}NavigationContent`}
                        aria-labelledby={camelizedTitle}
                        className="grid grid-cols-3 grid-flow-row-dense p-md bg-white shadow-lg rounded-t list-none"
                      >
                        {menuItem.sections.map((section, index) => (
                          <React.Fragment key={section.slug}>
                            <li
                              style={{
                                // Using style instead of className to avoid issue with dynamic grid-column-start/end.
                                gridColumnStart: index + 1,
                                gridColumnEnd: index + 2,
                              }}
                            >
                              <h3 className="gn-text-sm font-bold uppercase whitespace-nowrap">
                                {section.heading}
                              </h3>
                            </li>

                            {section.tags.map((tag) => (
                              <li
                                key={tag.slug}
                                className="mt-sm"
                                style={{
                                  // Using style instead of className to avoid issue with dynamic grid-column-start/end.
                                  gridColumnStart: index + 1,
                                  gridColumnEnd: index + 2,
                                }}
                              >
                                <NavigationMenuLink
                                  to={tag.slug}
                                  className="flex w-fit gn-text-sm hover:text-pink py-xs whitespace-nowrap focus:outline-none focus:ring-2 ring-mint"
                                  events={[
                                    {
                                      event: "Link Clicked",
                                      properties: {
                                        title: tag.name,
                                        sourceElement: SECTIONS_TYPES.NAV,
                                        sourceName: SECTIONS_TYPES.NAV,
                                        ...getUtmParamsFromQueryParams(
                                          router.query,
                                        ),
                                      },
                                    },
                                  ]}
                                >
                                  {tag.name}
                                </NavigationMenuLink>
                              </li>
                            ))}

                            <li
                              className="mt-sm"
                              style={{
                                // Using style instead of className to avoid issue with dynamic grid-column-start/end.
                                gridColumnStart: index + 1,
                                gridColumnEnd: index + 2,
                              }}
                            >
                              <NavigationMenuLink
                                to={section.slug}
                                className="block w-fit gn-text-sm font-bold py-xs hover:text-purple focus:outline-none focus:ring-2 ring-mint"
                                events={[
                                  {
                                    event: "Link Clicked",
                                    properties: {
                                      title: "Show All",
                                      sourceElement: SECTIONS_TYPES.NAV,
                                      sourceName: SECTIONS_TYPES.NAV,
                                      ...getUtmParamsFromQueryParams(
                                        router.query,
                                      ),
                                    },
                                  },
                                ]}
                                aria-label={`Show all ${section.heading}`}
                              >
                                Show All
                              </NavigationMenuLink>
                            </li>
                          </React.Fragment>
                        ))}

                        <li className="flex flex-row col-span-3 -m-md mt-md px-md py-sm bg-gray-100 justify-between items-center">
                          <NavigationMenuLink
                            to={`/${ROUTE_PREFIX.COMPANY}/games`}
                            className="hover:scale-150 focus:outline-mint"
                            events={[
                              {
                                event: "Link Clicked",
                                properties: {
                                  title: "GoNoodle Games",
                                  sourceElement: SECTIONS_TYPES.NAV,
                                  sourceName: SECTIONS_TYPES.NAV,
                                  ...getUtmParamsFromQueryParams(router.query),
                                },
                              },
                            ]}
                          >
                            <span className="sr-only">GoNoodle Games!</span>
                            <NextImage
                              src={GoNoodleGamesIcon}
                              className="inline-block mr-xs transform transition hover:scale-110 hover:-translate-y-xs"
                              alt="GoNoodle Games Logo"
                            />
                          </NavigationMenuLink>

                          <p className="gn-text-sm font-bold">
                            Games that get kids moving!
                          </p>

                          <NavigationMenuLink
                            to={`/${ROUTE_PREFIX.COMPANY}/games`}
                            className="text-black bg-white flex items-center justify-center hover:text-pink text-xs h-button-sm px-sm font-bold rounded transition-colors focus:outline-none focus:ring-2 ring-pink"
                            events={[
                              {
                                event: "Link Clicked",
                                properties: {
                                  title: "GoNoodle Games",
                                  sourceElement: SECTIONS_TYPES.NAV,
                                  sourceName: SECTIONS_TYPES.NAV,
                                  ...getUtmParamsFromQueryParams(router.query),
                                },
                              },
                            ]}
                          >
                            Learn More
                          </NavigationMenuLink>
                        </li>
                      </ul>
                    </NavigationMenu.Content>
                  </NavigationMenu.Item>
                );
              }

              return (
                <NavigationMenu.Item key={menuItem.title}>
                  <NavigationMenuLink
                    to={menuItem.sections[0].slug}
                    className="text-white rounded px-sm py-xs gn-text-sm font-bold mr-0.5 transition-colors focus:outline-none focus:ring-2 hover:ring-2 ring-mint"
                    events={[
                      {
                        event: "Link Clicked",
                        properties: {
                          title: `${menuItem.title} - Show All`,
                          sourceElement: SECTIONS_TYPES.NAV,
                          sourceName: SECTIONS_TYPES.NAV,
                          ...getUtmParamsFromQueryParams(router.query),
                        },
                      },
                    ]}
                  >
                    {menuItem.title}
                  </NavigationMenuLink>
                </NavigationMenu.Item>
              );
            })}

            <NavigationMenu.Item>
              <NavigationMenuLink
                to={
                  curriculum
                    ? `/${ROUTE_PREFIX.CURRICULUM}/${curriculum.id}`
                    : "https://supernoodle.gonoodle.com"
                }
                className="gn-text-sm font-bold text-transparent bg-clip-text bg-gradient-to-r from-yellow to-pink gn-text-sm rounded px-sm py-xs transition-colors focus:outline-none focus:ring-2 hover:ring-2 ring-mint"
                events={[
                  {
                    event: "Link Clicked",
                    properties: {
                      title: "SuperNoodle",
                      sourceElement: SECTIONS_TYPES.NAV,
                      sourceName: SECTIONS_TYPES.NAV,
                      ...getUtmParamsFromQueryParams(router.query),
                    },
                  },
                ]}
              >
                SuperNoodle
              </NavigationMenuLink>
            </NavigationMenu.Item>
          </NavigationMenu.List>

          <div className="absolute flex justify-center left-0 top-full mt-2 w-full">
            <NavigationMenu.Viewport
              forceMount
              className="w-[var(--radix-navigation-menu-viewport-width)] transition-all duration-700 "
              style={{
                // Avoid transitioning from initial position when first opening
                display: !activeTriggerOffset ? "none" : undefined,
                transform: `translateX(${activeTriggerOffset}px)`,
              }}
            />
          </div>
        </NavigationMenu.Root>
        {/* Desktop Nav End */}

        <div className="flex items-center justify-center">
          {!user.isLoggedIn ? (
            <>
              <Button
                variant="light"
                size="sm"
                onPress={() =>
                  router.push(
                    concatenateQueryParams(ROUTE_PATHS.REGISTRATION, {
                      referrer: JSURL.stringify({
                        pathname: router.pathname,
                        query: router.query,
                        sourceElement: SECTIONS_TYPES.NAV,
                        sourceName: SECTIONS.CREATE_ACCOUNT_BUTTON,
                      }),
                    }),
                    ROUTE_PATHS.REGISTRATION,
                  )
                }
                className="mx-xs font-regular lg:whitespace-nowrap"
              >
                Create a Free Account
              </Button>
              <Button
                variant="light-outline"
                size="sm"
                onPress={() =>
                  router.push(
                    concatenateQueryParams(ROUTE_PATHS.LOGIN, {
                      referrer: JSURL.stringify({
                        pathname: router.pathname,
                        query: router.query,
                        sourceElement: SECTIONS_TYPES.NAV,
                        sourceName: SECTIONS.LOGIN_BUTTON,
                      }),
                    }),
                    ROUTE_PATHS.LOGIN,
                  )
                }
                className="mx-xs font-regular lg:whitespace-nowrap"
              >
                Log In
              </Button>
            </>
          ) : (
            <Popover.Root
              offset={18} // This offset is calculated by (h-navbar/2) - trigger height (h-9)
              placement="bottom end"
              shouldFlip={false}
              isOpen={isUserMenuOpen}
              onOpen={() => setIsUserMenuOpen(true)}
              onClose={() => setIsUserMenuOpen(false)}
            >
              <Popover.Trigger
                ariaLabel={
                  isUserMenuOpen ? "Close profile menu" : "Open profile menu"
                }
                className="focus:ring rounded-full focus:ring-white focus:ring-offset-2 focus:ring-offset-gray-900 focus:outline-none"
              >
                <div className="block lg:hidden">
                  <div
                    className={`h-9 w-9 text-white hover:text-mint flex items-center justify-center
                  ${!isUserMenuOpen && "hidden"}`}
                  >
                    <span className="sr-only">Close profile menu</span>
                    <XIcon
                      className="block h-7 w-7 text-center"
                      aria-hidden="true"
                    />
                  </div>
                  <div
                    className={`w-9 h-9 rounded-full relative overflow-hidden bg-purple ${
                      isUserMenuOpen && "hidden"
                    }`}
                  >
                    {champ && (
                      <Image
                        className="absolute inset-0 w-full h-full object-cover"
                        sources={{
                          "regular@1x": champ.images.headshot["1x"],
                          "regular@2x": champ.images.headshot["2x"],
                          "regular@3x": champ.images.headshot["3x"],
                        }}
                        alt=""
                      />
                    )}
                  </div>
                </div>
                <div className="hidden lg:block">
                  <div className="w-9 h-9 rounded-full relative overflow-hidden bg-purple">
                    {champ && (
                      <Image
                        className="absolute inset-0 w-full h-full object-cover"
                        sources={{
                          "regular@1x": champ.images.headshot["1x"],
                          "regular@2x": champ.images.headshot["2x"],
                          "regular@3x": champ.images.headshot["3x"],
                        }}
                        alt=""
                      />
                    )}
                  </div>
                </div>
              </Popover.Trigger>

              <Popover.Content className="flex flex-col w-full lg:w-fit lg:-ml-7 lg:rounded overflow-hidden focus:ring focus:ring-purple focus:outline-none z-30">
                <div className="rounded bg-gray-300 space-y-px flex flex-col lg:min-w-[300px]">
                  <div className="flex flex-col items-center p-5 bg-white">
                    {champ && (
                      <Image
                        className="w-52 h-52 object-contain mb-4"
                        sources={{
                          "regular@1x": champ.images.full["1x"],
                          "regular@2x": champ.images.full["2x"],
                          "regular@3x": champ.images.full["3x"],
                        }}
                        alt={champ.name}
                      />
                    )}
                    <h2 className="font-bold text-lg text-center leading-7 max-w-60 text-ellipsis overflow-hidden">
                      {profile?.name}
                    </h2>
                    <p className="text-center">
                      {profile?.classroomType === PROFILE_TYPES.CLASSROOM
                        ? `${profile?.size} ${
                            profile?.size > 1 ? "students" : "student"
                          } ${
                            profile?.school.id !== null
                              ? `at ${profile?.school.name}`
                              : ""
                          }`
                        : profile?.grade.name}
                    </p>
                    <Link
                      to={`/${ROUTE_PREFIX.CHAMP_COLLECTION}`}
                      queryParams={{
                        referrer: {
                          pathname: router.pathname,
                          query: router.query,
                          sourceElement: SECTIONS_TYPES.NAV,
                          sourceName: SECTIONS_TYPES.NAV,
                        },
                      }}
                      className="font-semibold text-sm rounded p-xs focus:ring focus:ring-purple focus:outline-none text-purple "
                    >
                      View Champ Collection
                    </Link>
                  </div>
                  <div className="flex flex-col space-y-1 p-3 bg-white">
                    <Link
                      to={`/${ROUTE_PREFIX.PROFILES}`}
                      queryParams={{
                        referrer: {
                          pathname: router.pathname,
                          query: router.query,
                          sourceElement: SECTIONS_TYPES.NAV,
                          sourceName: SECTIONS_TYPES.NAV,
                        },
                      }}
                      className="flex gap-3 items-center font-semibold text-sm hover:bg-gray-100 rounded p-sm focus:ring focus:ring-purple focus:outline-none"
                    >
                      <SwitchHorizontalIcon
                        className="text-purple w-4 h-4"
                        aria-hidden="true"
                      />
                      Manage Profiles
                    </Link>
                    <Link
                      to={`/${ROUTE_PREFIX.ACCOUNT_SETTINGS}`}
                      className="flex gap-3 items-center font-semibold text-sm hover:bg-gray-100 rounded p-sm focus:ring focus:ring-purple focus:outline-none"
                    >
                      <UserIcon
                        className="text-purple w-4 h-4"
                        aria-hidden="true"
                      />
                      Account Settings
                    </Link>
                    <AgeGatedPressable
                      onPress={() =>
                        new Promise(() => {
                          router.push("https://support.gonoodle.com");
                        })
                      }
                      className="flex gap-3 items-center font-semibold text-sm w-full text-left hover:bg-gray-100 rounded p-sm focus:ring focus:ring-purple focus:outline-none"
                    >
                      <QuestionMarkCircleIcon
                        className="text-purple w-4 h-4"
                        aria-hidden="true"
                      />
                      Help Center for GrownUps
                    </AgeGatedPressable>
                    <Pressable
                      className="flex gap-3 items-center font-semibold text-sm w-full text-left hover:bg-gray-100 rounded p-sm focus:ring focus:ring-purple focus:outline-none"
                      onPress={() => {
                        logLogoutEvent();
                        logout();
                      }}
                    >
                      <LogoutIcon
                        className="text-purple w-4 h-4"
                        aria-hidden="true"
                      />
                      Log Out
                    </Pressable>
                  </div>
                </div>
              </Popover.Content>
            </Popover.Root>
          )}

          {user.isLoggedIn && (
            <Link
              to={`/${ROUTE_PREFIX.FAVORITES}`}
              className="ml-4 flex-shrink-0"
            >
              <span className="text-white hover:text-mint p-xs">
                <span className="sr-only">Favorites</span>
                <HeartIcon className="h-7 w-7" aria-hidden="true" />
              </span>
            </Link>
          )}

          <div className="hidden lg:block py-6 border-l rounded-full border-gray-600 mx-4" />

          <Link
            to={`/${ROUTE_PREFIX.SEARCH}`}
            className="ml-3 p-xs flex flex-row justify-center space-x-3 flex-shrink-0 lg:ml-0 text-white hover:text-mint focus:text-mint"
          >
            <SearchIcon className="h-7 w-7" aria-hidden="true" />
            <span className="hidden lg:block text-md font-bold">Search</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
